
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChevronRight, Search, Percent } from 'lucide-react';
import { SearchableSelect } from '@/components/SearchableSelect';
import api from '@/lib/api';
import { fmt, MONTH_OPTIONS } from './constants';

export function ProjectPLTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [plMonth, setPlMonth] = useState('all');
  const [plSearch, setPlSearch] = useState('');
  const [discountSummary, setDiscountSummary] = useState(null);

  const loadPL = useCallback((month) => {
    setLoading(true);
    const url = month && month !== 'all' ? `/accounting/project-profitability?billable_month=${month}` : '/accounting/project-profitability';
    api.get(url)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { loadPL(plMonth); }, [loadPL, plMonth]);
  useEffect(() => {
    api.get('/accounting/discount-summary').then(r => setDiscountSummary(r.data)).catch(() => setDiscountSummary(null));
  }, []);

  if (loading) return <div className="animate-pulse text-muted-foreground p-4">Loading...</div>;

  const toggle = (pid) => setExpanded(prev => ({ ...prev, [pid]: !prev[pid] }));

  // Aggregate totals
  const totals = data.reduce((acc, p) => ({
    billed: acc.billed + p.total_billed,
    labor: acc.labor + p.labor_cost,
    actualLabor: acc.actualLabor + (p.actual_labor_cost || 0),
    billableLabor: acc.billableLabor + (p.billable_labor_cost || 0),
    reimb: acc.reimb + p.reimbursement_cost,
    cost: acc.cost + p.total_cost,
    profit: acc.profit + p.profit,
    collected: acc.collected + p.total_collected,
  }), { billed: 0, labor: 0, actualLabor: 0, billableLabor: 0, reimb: 0, cost: 0, profit: 0, collected: 0 });

  const overallMargin = totals.billed > 0 ? ((totals.profit / totals.billed) * 100).toFixed(1) : 0;

  const marginColor = (m) => m >= 60 ? 'text-emerald-400' : m >= 30 ? 'text-amber-400' : m > 0 ? 'text-orange-400' : 'text-rose-400';
  const profitColor = (v) => v > 0 ? 'text-emerald-400' : v < 0 ? 'text-rose-400' : 'text-muted-foreground';

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading font-bold text-lg" data-testid="project-pl-heading">Project P&L</h2>
          <p className="text-xs text-muted-foreground mt-1">Revenue vs labor & reimbursement costs per project and section</p>
        </div>
        <SearchableSelect
          value={plMonth}
          onValueChange={setPlMonth}
          options={MONTH_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
          placeholder="All Months"
          searchPlaceholder="Search months..."
          className="w-48"
          data-testid="pl-month-filter"
        />
      </div>

      {/* Search + Summary Cards */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search projects..." value={plSearch} onChange={e => setPlSearch(e.target.value)} className="pl-9 bg-card border-border w-56" data-testid="pl-search" />
        </div>
        {plSearch && <button className="text-xs text-primary hover:underline" onClick={() => setPlSearch('')}>Clear</button>}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="pl-summary-cards">
        {[
          { label: 'Total Billed', value: fmt(totals.billed), color: 'text-primary' },
          { label: 'Collected', value: fmt(totals.collected), color: 'text-emerald-400' },
          // Labor Cost renders two stacked values ("Actual" over "Billable")
          // inside the same card. Colors match the table cell below so the
          // reader learns the coding once.
          {
            label: 'Labor Cost',
            dual: true,
            actual: fmt(totals.actualLabor),
            billable: fmt(totals.billableLabor),
          },
          { label: 'Reimb. Cost', value: fmt(totals.reimb), color: 'text-violet-400' },
          { label: 'Net Profit', value: fmt(totals.profit), color: profitColor(totals.profit) },
          { label: 'Margin', value: `${overallMargin}%`, color: marginColor(Number(overallMargin)) },
        ].map(c => (
          <Card key={c.label} className="border-border bg-card shadow-none">
            <CardContent className="p-3 text-center">
              <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">{c.label}</p>
              {c.dual ? (
                <div className="mt-1" data-testid="pl-card-labor-cost-dual">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Actual</p>
                  <p className="text-lg font-bold tabular-nums text-cyan-400" data-testid="pl-card-labor-actual">{c.actual}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Billable</p>
                  <p className="text-lg font-bold tabular-nums text-slate-400" data-testid="pl-card-labor-billable">{c.billable}</p>
                </div>
              ) : (
                <p className={`text-lg font-bold mt-1 tabular-nums ${c.color}`}>{c.value}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Discount Summary Widget */}
      {discountSummary && (
        <Card className="border-amber-500/20 bg-amber-500/5 shadow-none" data-testid="discount-summary-widget">
          <CardContent className="p-3 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-md flex items-center justify-center bg-amber-500/15 shrink-0">
                <Percent className="h-4 w-4 text-amber-400" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">Early-Pay &amp; Invoice Discounts · {discountSummary.year} YTD</p>
                {discountSummary.invoice_count === 0 ? (
                  <p className="text-sm mt-1 text-muted-foreground">No discounts applied yet this year.</p>
                ) : (
                  <p className="text-sm mt-1">
                    <span className="text-amber-400 font-heading font-bold tabular-nums">{fmt(discountSummary.total_discount)}</span>
                    <span className="text-muted-foreground"> given across </span>
                    <span className="text-foreground font-medium">{discountSummary.invoice_count} invoice{discountSummary.invoice_count === 1 ? '' : 's'}</span>
                    <span className="text-muted-foreground"> · </span>
                    <span className="text-foreground font-medium">{discountSummary.percent_of_billed}%</span>
                    <span className="text-muted-foreground"> of billed revenue ({fmt(discountSummary.total_billed)})</span>
                  </p>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground max-w-xs text-right">Posted to <span className="text-amber-400">Sales Discounts</span> (contra-revenue). Lower % = better terms held; higher % = deeper concessions.</p>
          </CardContent>
        </Card>
      )}

      {/* Project Table */}
      <Card className="border-border bg-card shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider w-[24px]"></TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Project</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Hours</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Labor Cost</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Reimb. Cost</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Total Cost</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Billed</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Profit</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Margin</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground text-xs py-6">No projects with financial activity yet.</TableCell></TableRow>
              ) : data.filter(p =>
                !plSearch || `${p.project_number} ${p.project_name}`.toLowerCase().includes(plSearch.toLowerCase())
              ).map(p => (
                <React.Fragment key={p.project_id}>
                  <TableRow
                    className="border-border cursor-pointer hover:bg-secondary/30 transition-colors"
                    onClick={() => toggle(p.project_id)}
                    data-testid={`project-pl-row-${p.project_id}`}
                  >
                    <TableCell className="text-xs px-2">
                      <ChevronRight className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${expanded[p.project_id] ? 'rotate-90' : ''}`} />
                    </TableCell>
                    <TableCell className="text-xs font-medium">{p.project_number} - {p.project_name}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{p.status}</Badge></TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{(p.total_hours || 0).toFixed(2)}h</TableCell>
                    <TableCell className="text-xs text-right tabular-nums" data-testid={`pl-row-labor-${p.project_id}`}>
                      <div className="flex flex-col items-end leading-tight">
                        <span className="text-cyan-400" data-testid={`pl-row-labor-actual-${p.project_id}`}>{fmt(p.actual_labor_cost)}</span>
                        <span className="text-slate-400" data-testid={`pl-row-labor-billable-${p.project_id}`}>{fmt(p.billable_labor_cost)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-right tabular-nums text-violet-400">{fmt(p.reimbursement_cost)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums font-medium">{fmt(p.total_cost)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{fmt(p.total_billed)}</TableCell>
                    <TableCell className={`text-xs text-right tabular-nums font-bold ${profitColor(p.profit)}`}>{fmt(p.profit)}</TableCell>
                    <TableCell className={`text-xs text-right tabular-nums font-bold ${marginColor(p.margin)}`}>{p.margin}%</TableCell>
                  </TableRow>
                  {expanded[p.project_id] && p.sections.filter(s => s.hours > 0 || s.billed > 0 || s.total_cost > 0).length > 0 && (
                    p.sections.filter(s => s.hours > 0 || s.billed > 0 || s.total_cost > 0).map(s => (
                      <TableRow key={s.section_id} className="border-border bg-secondary/10" data-testid={`section-pl-${s.section_id}`}>
                        <TableCell></TableCell>
                        <TableCell className="text-xs text-muted-foreground pl-6">
                          {s.section_number ? `${s.section_number} - ` : ''}{s.section_name}
                          <span className="text-xs ml-2 opacity-50">(Fee: {fmt(s.fee_amount)})</span>
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-xs text-right tabular-nums text-muted-foreground">{(s.hours || 0).toFixed(2)}h</TableCell>
                        <TableCell className="text-xs text-right tabular-nums" data-testid={`pl-section-labor-${s.section_id}`}>
                          <div className="flex flex-col items-end leading-tight">
                            <span className="text-cyan-400/70">{fmt(s.actual_labor_cost)}</span>
                            <span className="text-slate-400/70">{fmt(s.billable_labor_cost)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-right tabular-nums text-violet-400/70">{fmt(s.reimbursement_cost)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums text-muted-foreground">{fmt(s.total_cost)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums text-muted-foreground">{fmt(s.billed)}</TableCell>
                        <TableCell className={`text-xs text-right tabular-nums ${profitColor(s.profit)}`}>{fmt(s.profit)}</TableCell>
                        <TableCell className={`text-xs text-right tabular-nums ${marginColor(s.margin)}`}>{s.margin}%</TableCell>
                      </TableRow>
                    ))
                  )}
                  {expanded[p.project_id] && p.sections.filter(s => s.hours > 0 || s.billed > 0 || s.total_cost > 0).length === 0 && (
                    <TableRow key={`${p.project_id}-empty`} className="border-border bg-secondary/10">
                      <TableCell></TableCell>
                      <TableCell colSpan={9} className="text-xs text-muted-foreground text-center py-3 italic">No section-level cost data yet</TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
